import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * UserDetailDrawer — slide-over panel for the selected user row.
 *
 * Three tabs:
 *   1. Profile   — display_name, phone, timezone, locale, is_oof
 *                  Sends PATCH /admin/users/{id} with `If-Match: <version>`.
 *   2. Roles     — RolePicker → PUT /admin/users/{id}/roles
 *   3. Activity  — invited_at + last_signin_at; future: per-user audit
 *
 * Deactivate sits in the footer; soft-deletes via POST .../deactivate.
 *
 * Layout: implemented as a Modal with size=xl rather than a true side
 * drawer because the existing primitive set has no Drawer and the Modal
 * already handles escape-to-close + click-outside-to-close. Tradeoff
 * documented; if more flows want a drawer we'll promote Modal.tsx then.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t as tr } from '@/lib/i18n';
import { useEffect, useState } from 'react';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { ApiError, api } from '@/lib/api';
import { RolePicker } from './RolePicker';
export function UserDetailDrawer({ userId, onClose, }) {
    const qc = useQueryClient();
    const toast = useToast();
    const open = userId !== null;
    const user = useQuery({
        queryKey: ['admin-user', userId],
        queryFn: () => api.get(`/admin/users/${userId}`),
        enabled: open,
    });
    const [tab, setTab] = useState('profile');
    useEffect(() => {
        if (open)
            setTab('profile');
    }, [open, userId]);
    if (!open)
        return null;
    return (_jsxs(Modal, { open: true, onClose: onClose, title: user.data?.display_name ?? 'User', size: "xl", children: [user.isLoading && _jsx("div", { className: "text-sm text-slate-500", children: "Loading\u2026" }), user.isError && (_jsx("div", { className: "text-sm text-rose-600", children: user.error.message })), user.data && (_jsxs("div", { className: "space-y-5", children: [_jsx(Header, { user: user.data }), _jsx(TabStrip, { tab: tab, setTab: setTab }), tab === 'profile' && (_jsx(ProfileTab, { user: user.data, onSaved: () => {
                            qc.invalidateQueries({ queryKey: ['admin-user', userId] });
                            qc.invalidateQueries({ queryKey: ['admin-users'] });
                        } })), tab === 'roles' && (_jsx(RolesTab, { user: user.data, onSaved: () => {
                            qc.invalidateQueries({ queryKey: ['admin-user', userId] });
                            qc.invalidateQueries({ queryKey: ['admin-users'] });
                        } })), tab === 'activity' && _jsx(ActivityTab, { user: user.data }), _jsx(Footer, { user: user.data, onDeactivated: () => {
                            toast.success(tr('User deactivated'), `${user.data.email} has been disabled.`);
                            qc.invalidateQueries({ queryKey: ['admin-users'] });
                            onClose();
                        }, onInviteResent: () => {
                            toast.success('Invite resent', `A fresh invite email is on its way to ${user.data.email}.`);
                        }, onError: (msg) => toast.error(tr('Action failed'), msg) })] }))] }));
}
// ---- Header ----------------------------------------------------------------
function Header({ user }) {
    return (_jsxs("div", { className: "grid gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-sm dark:border-slate-800 dark:bg-slate-900/40 sm:grid-cols-2", children: [_jsxs("div", { children: [_jsx("div", { className: "text-xs uppercase tracking-wide text-slate-500", children: "Email" }), _jsx("div", { className: "font-medium", children: user.email })] }), _jsxs("div", { children: [_jsx("div", { className: "text-xs uppercase tracking-wide text-slate-500", children: "Status" }), _jsx("div", { children: _jsx(StatusBadge, { status: user.status }) })] }), _jsxs("div", { children: [_jsx("div", { className: "text-xs uppercase tracking-wide text-slate-500", children: "Type" }), _jsx("div", { children: user.is_internal ? 'Internal' : 'External' })] }), _jsxs("div", { children: [_jsx("div", { className: "text-xs uppercase tracking-wide text-slate-500", children: "Current roles" }), _jsxs("div", { className: "mt-0.5 flex flex-wrap gap-1", children: [user.roles.length === 0 && _jsx("span", { className: "text-xs text-slate-400", children: "\u2014 none \u2014" }), user.roles.map((r) => (_jsx(Badge, { children: r.name }, r.id)))] })] })] }));
}
// ---- Tabs ------------------------------------------------------------------
function TabStrip({ tab, setTab }) {
    const tabs = [
        { key: 'profile', label: 'Profile' },
        { key: 'roles', label: 'Roles' },
        { key: 'activity', label: 'Activity' },
    ];
    return (_jsx("div", { className: "flex border-b border-slate-200 dark:border-slate-800", children: tabs.map((t) => (_jsx("button", { type: "button", onClick: () => setTab(t.key), className: [
                '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition',
                tab === t.key
                    ? 'border-brand-500 text-brand-700 dark:text-brand-300'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
            ].join(' '), children: t.label }, t.key))) }));
}
// ---- Profile tab -----------------------------------------------------------
function ProfileTab({ user, onSaved }) {
    const toast = useToast();
    const [displayName, setDisplayName] = useState(user.display_name);
    const [phone, setPhone] = useState(user.phone ?? '');
    const [timezone, setTimezone] = useState(user.timezone ?? '');
    const [locale, setLocale] = useState(user.locale ?? '');
    const [isOof, setIsOof] = useState(user.is_oof);
    useEffect(() => {
        setDisplayName(user.display_name);
        setPhone(user.phone ?? '');
        setTimezone(user.timezone ?? '');
        setLocale(user.locale ?? '');
        setIsOof(user.is_oof);
        // Intentional: reset form state only when the user record's identity or
        // server-side version actually changes (i.e. a different user is opened
        // or our save bumped the version). Listing every field in deps would
        // clobber the user's in-progress edits on every background refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id, user.version]);
    const save = useMutation({
        mutationFn: (body) => api.patch(`/admin/users/${user.id}`, body, { 'If-Match': String(user.version) }),
        onSuccess: () => {
            toast.success(tr('Profile updated'));
            onSaved();
        },
        onError: (err) => {
            // 409 is the optimistic-concurrency miss — friendlier message for that case.
            if (err.status === 409) {
                toast.error(tr('Stale view'), tr('Another change came in first. Reloading the latest version.'));
                onSaved();
            }
            else {
                toast.error(tr('Save failed'), err.message);
            }
        },
    });
    return (_jsxs("form", { onSubmit: (e) => {
            e.preventDefault();
            const body = {
                display_name: displayName.trim(),
                phone: phone.trim() || null,
                timezone: timezone.trim() || null,
                locale: locale.trim() || null,
                is_oof: isOof,
            };
            save.mutate(body);
        }, className: "space-y-4", children: [_jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Field, { label: tr('Display name'), required: true, children: _jsx(Input, { value: displayName, onChange: (e) => setDisplayName(e.target.value), required: true }) }), _jsx(Field, { label: tr('Phone'), children: _jsx(Input, { value: phone, onChange: (e) => setPhone(e.target.value) }) }), _jsx(Field, { label: tr('Timezone'), hint: tr('IANA name, e.g. Europe/London'), children: _jsx(Input, { value: timezone, onChange: (e) => setTimezone(e.target.value) }) }), _jsx(Field, { label: tr('Locale'), hint: tr('BCP-47, e.g. en-GB'), children: _jsx(Input, { value: locale, onChange: (e) => setLocale(e.target.value), maxLength: 10 }) })] }), _jsxs("label", { className: "inline-flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", className: "h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500", checked: isOof, onChange: (e) => setIsOof(e.target.checked) }), "Out of office (hidden from assignable-users picker)"] }), _jsx("div", { className: "flex justify-end", children: _jsx(Button, { type: "submit", disabled: save.isPending, children: save.isPending ? 'Saving…' : 'Save profile' }) })] }));
}
// ---- Roles tab -------------------------------------------------------------
function RolesTab({ user, onSaved }) {
    const toast = useToast();
    const [roleIds, setRoleIds] = useState(user.roles.map((r) => r.id));
    useEffect(() => {
        setRoleIds(user.roles.map((r) => r.id));
    }, [user.id, user.roles]);
    const save = useMutation({
        mutationFn: (body) => api.put(`/admin/users/${user.id}/roles`, body),
        onSuccess: () => {
            toast.success(tr('Roles updated'));
            onSaved();
        },
        onError: (err) => toast.error(tr('Role save failed'), err.message),
    });
    const dirty = roleIds.length !== user.roles.length ||
        roleIds.some((id) => !user.roles.find((r) => r.id === id));
    return (_jsxs("div", { className: "space-y-4", children: [_jsx(RolePicker, { value: roleIds, onChange: setRoleIds, disabled: save.isPending }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { type: "button", variant: "ghost", onClick: () => setRoleIds(user.roles.map((r) => r.id)), disabled: !dirty || save.isPending, children: tr('Reset') }), _jsx(Button, { type: "button", onClick: () => save.mutate({ role_ids: roleIds }), disabled: !dirty || save.isPending, children: save.isPending ? 'Saving…' : 'Save roles' })] })] }));
}
// ---- Activity tab ----------------------------------------------------------
function ActivityTab({ user }) {
    return (_jsxs("dl", { className: "grid gap-y-2 gap-x-6 text-sm sm:grid-cols-2", children: [_jsx(Row, { label: tr('Invited at'), value: formatTs(user.invited_at) }), _jsx(Row, { label: tr('Last sign-in'), value: formatTs(user.last_signin_at) }), _jsx(Row, { label: tr('Created at'), value: formatTs(user.created_at) }), _jsx(Row, { label: tr('Updated at'), value: formatTs(user.updated_at) }), _jsx(Row, { label: tr('Version (concurrency)'), value: String(user.version) }), _jsx(Row, { label: tr('Entra binding'), value: user.status === 'invited' ? 'pending first sign-in' : 'bound' })] }));
}
function Row({ label, value }) {
    return (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-xs uppercase tracking-wide text-slate-500", children: label }), _jsx("dd", { className: "font-medium text-slate-900 dark:text-slate-100", children: value })] }));
}
function formatTs(iso) {
    if (!iso)
        return '—';
    try {
        return new Date(iso).toLocaleString();
    }
    catch {
        return iso;
    }
}
// ---- Footer ----------------------------------------------------------------
function Footer({ user, onDeactivated, onInviteResent, onError, }) {
    const deactivate = useMutation({
        mutationFn: () => api.post(`/admin/users/${user.id}/deactivate`),
        onSuccess: () => onDeactivated(),
        onError: (err) => onError(err.message),
    });
    const resendInvite = useMutation({
        mutationFn: () => api.post(`/admin/users/${user.id}/resend-invite`),
        onSuccess: () => onInviteResent(),
        onError: (err) => onError(err.message),
    });
    if (user.status === 'deactivated') {
        return (_jsx("div", { className: "border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-800", children: "This user is already deactivated. Re-activation requires a fresh invite." }));
    }
    const canResend = user.status === 'invited';
    return (_jsxs("div", { className: "flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-4 dark:border-slate-800", children: [_jsx(Button, { type: "button", variant: "danger", onClick: () => {
                    if (window.confirm(`Deactivate ${user.email}? They will lose access immediately.`)) {
                        deactivate.mutate();
                    }
                }, disabled: deactivate.isPending, children: deactivate.isPending ? 'Deactivating…' : 'Deactivate user' }), canResend && (_jsx(Button, { type: "button", variant: "secondary", onClick: () => resendInvite.mutate(), disabled: resendInvite.isPending, title: tr("Re-queue the invite email — useful if the original wasn't delivered."), children: resendInvite.isPending ? 'Resending…' : 'Resend invite' }))] }));
}
